import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiPackage,
  FiSearch,
  FiPlusCircle,
  FiRefreshCw,
  FiEye,
  FiTrash2,
  FiChevronRight,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import escalationService from "../../services/escalationService";

const getStatusBadge = (status) => {
  const map = {
    submitted: { bg: "#e8f1f8", text: "#002747", border: "#b6d5ec", label: "Submitted" },
    under_review: { bg: "#fef3c7", text: "#b45309", border: "#fde68a", label: "Under Review" },
    action_taken: { bg: "#f3e8ff", text: "#6b21a8", border: "#e9d5ff", label: "Action Taken" },
    in_progress: { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd", label: "In Progress" },
    approved: { bg: "#ecfdf5", text: "#047857", border: "#a7f3d0", label: "Approved" },
    partially_approved: { bg: "#fef9c3", text: "#854d0e", border: "#fef08a", label: "Partially Approved" },
    rejected: { bg: "#fef2f2", text: "#b91c1c", border: "#fecaca", label: "Rejected" },
    resolved: { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", label: "Resolved" },
    closed: { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: "Closed" },
  };
  const config = map[status] || { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1", label: status };
  return (
    <span
      style={{
        fontSize: "11px",
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: "999px",
        backgroundColor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: config.text }} />
      {config.label}
    </span>
  );
};

const getPriorityBadge = (priority) => {
  const map = {
    urgent: { bg: "#fee2e2", text: "#991b1b", label: "Urgent" },
    high: { bg: "#ffedd5", text: "#9a3412", label: "High" },
    medium: { bg: "#e0f2fe", text: "#075985", label: "Medium" },
    low: { bg: "#f1f5f9", text: "#475569", label: "Low" },
  };
  const c = map[priority] || map.medium;
  return (
    <span
      style={{
        fontSize: "10.5px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        padding: "2px 7px",
        borderRadius: "4px",
        backgroundColor: c.bg,
        color: c.text,
      }}
    >
      {c.label}
    </span>
  );
};

export default function EscalationList() {
  const { user } = useApp();
  const isSuperAdmin = user?.role === "superadmin";
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("all"); // 'all', 'escalation', 'resource_request', 'pending', 'in_action', 'resolved'
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [pagination, setPagination] = useState({ page: 1, limit: 15, totalPages: 1, total: 0 });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (activeTab === "escalation" || activeTab === "resource_request") {
        params.type = activeTab;
      } else if (["pending", "in_action", "resolved"].includes(activeTab)) {
        params.status = activeTab;
      }

      if (priorityFilter !== "all") {
        params.priority = priorityFilter;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const [listRes, statsRes] = await Promise.all([
        escalationService.getAll(params),
        escalationService.getStats(),
      ]);

      const items = Array.isArray(listRes?.data) ? listRes.data : (Array.isArray(listRes) ? listRes : []);
      setRequests(items);

      if (listRes?.pagination) {
        setPagination((prev) => ({
          ...prev,
          totalPages: listRes.pagination.totalPages || 1,
          total: listRes.pagination.total || items.length,
        }));
      }

      if (statsRes) {
        setStats(statsRes.stats || statsRes.data || statsRes);
      }
    } catch (error) {
      console.error("Failed to load escalations:", error);
      toast.error("Failed to load escalations/requests.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, priorityFilter, searchTerm, pagination.page, pagination.limit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete/cancel this request?")) return;
    try {
      const res = await escalationService.delete(id);
      if (res?.success || res?.status === 200) {
        toast.success("Request deleted successfully.");
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to delete request.");
    }
  };

  return (
    <div className="page-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "1.5rem 1rem" }}>
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#64748b", fontSize: "13px", marginBottom: "4px" }}>
            <Link to={isSuperAdmin ? "/superadmin" : "/dashboard"} style={{ color: "#64748b" }}>
              {isSuperAdmin ? "SuperAdmin Hub" : "Dashboard"}
            </Link>
            <FiChevronRight size={13} />
            <span style={{ color: "var(--text, #1e293b)", fontWeight: 600 }}>Escalations & Requests</span>
          </div>
          <h1 style={{ fontSize: "22px", fontWeight: 800, margin: 0, color: "var(--text, #0f172a)" }}>
            Matter Escalations & Resource Requests
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "var(--text-muted, #64748b)" }}>
            {isSuperAdmin
              ? "Review college issues, administrative escalations, and resource requisition requests across institutions."
              : "Escalate matters that require Super Admin intervention or submit requisitions for equipment & college resources."}
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={loadData}
            title="Refresh list"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiRefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
          </button>
          {!isSuperAdmin && (
            <Link
              to="/escalations/new"
              className="btn btn-primary"
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FiPlusCircle size={15} /> New Request / Escalation
            </Link>
          )}
        </div>
      </div>

      {/* ── Stat Counters ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.85rem",
          marginBottom: "1.5rem",
        }}
      >
        <div
          onClick={() => setActiveTab("all")}
          style={{
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            backgroundColor: "#fff",
            border: activeTab === "all" ? "2px solid #6366f1" : "1px solid #e2e8f0",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: "11.5px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
            Total Requests
          </div>
          <div style={{ fontSize: "22px", fontWeight: 800, color: "#1e293b", marginTop: "2px" }}>
            {stats?.total || 0}
          </div>
        </div>

        <div
          onClick={() => setActiveTab("escalation")}
          style={{
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            backgroundColor: "#fff",
            border: activeTab === "escalation" ? "2px solid #f59e0b" : "1px solid #e2e8f0",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: "11.5px", fontWeight: 700, color: "#b45309", textTransform: "uppercase" }}>
            Matter Escalations
          </div>
          <div style={{ fontSize: "22px", fontWeight: 800, color: "#92400e", marginTop: "2px" }}>
            {stats?.escalations || 0}
          </div>
        </div>

        <div
          onClick={() => setActiveTab("resource_request")}
          style={{
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            backgroundColor: "#fff",
            border: activeTab === "resource_request" ? "2px solid #3b82f6" : "1px solid #e2e8f0",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: "11.5px", fontWeight: 700, color: "#002747", textTransform: "uppercase" }}>
            Resource Requests
          </div>
          <div style={{ fontSize: "22px", fontWeight: 800, color: "#1e3a8a", marginTop: "2px" }}>
            {stats?.resourceRequests || 0}
          </div>
        </div>

        <div
          onClick={() => setActiveTab("pending")}
          style={{
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            backgroundColor: "#fff",
            border: activeTab === "pending" ? "2px solid #eab308" : "1px solid #e2e8f0",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: "11.5px", fontWeight: 700, color: "#a16207", textTransform: "uppercase" }}>
            Pending Review
          </div>
          <div style={{ fontSize: "22px", fontWeight: 800, color: "#854d0e", marginTop: "2px" }}>
            {stats?.pending || 0}
          </div>
        </div>

        <div
          onClick={() => setActiveTab("in_action")}
          style={{
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            backgroundColor: "#fff",
            border: activeTab === "in_action" ? "2px solid #8b5cf6" : "1px solid #e2e8f0",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: "11.5px", fontWeight: 700, color: "#6d28d9", textTransform: "uppercase" }}>
            Action In Progress
          </div>
          <div style={{ fontSize: "22px", fontWeight: 800, color: "#4c1d95", marginTop: "2px" }}>
            {stats?.inAction || 0}
          </div>
        </div>

        <div
          onClick={() => setActiveTab("resolved")}
          style={{
            padding: "0.85rem 1rem",
            borderRadius: "10px",
            backgroundColor: "#fff",
            border: activeTab === "resolved" ? "2px solid #10b981" : "1px solid #e2e8f0",
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <div style={{ fontSize: "11.5px", fontWeight: 700, color: "#047857", textTransform: "uppercase" }}>
            Resolved / Approved
          </div>
          <div style={{ fontSize: "22px", fontWeight: 800, color: "#064e3b", marginTop: "2px" }}>
            {stats?.resolved || 0}
          </div>
        </div>
      </div>

      {/* ── Filters & Tabs ── */}
      <div
        className="card"
        style={{
          padding: "1rem 1.2rem",
          marginBottom: "1.25rem",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          {/* Tabs */}
          <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
            {[
              { id: "all", label: "All" },
              { id: "escalation", label: "Escalations" },
              { id: "resource_request", label: "Resources" },
              { id: "pending", label: "Pending Review" },
              { id: "in_action", label: "Action In Progress" },
              { id: "resolved", label: "Resolved" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                style={{
                  padding: "5px 12px",
                  borderRadius: "6px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: "none",
                  backgroundColor: activeTab === tab.id ? "#1e293b" : "#f1f5f9",
                  color: activeTab === tab.id ? "#fff" : "#475569",
                  transition: "all 0.15s ease",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search & Priority Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <div style={{ position: "relative", minWidth: 220 }}>
              <FiSearch
                size={14}
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
              />
              <input
                type="text"
                placeholder="Search ticket, title..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="form-control"
                style={{ paddingLeft: "30px", fontSize: "12.5px", height: "34px" }}
              />
            </div>

            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className="form-control"
              style={{ fontSize: "12.5px", height: "34px", width: "auto" }}
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Requests Table / List ── */}
      <div
        className="card"
        style={{
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#64748b" }}>
            <div className="spinner" style={{ margin: "0 auto 10px" }} />
            Loading escalations & requests…
          </div>
        ) : requests.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3.5rem 1rem" }}>
            <FiPackage size={40} color="#cbd5e1" style={{ marginBottom: "8px" }} />
            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#334155", margin: 0 }}>
              No requests found
            </h3>
            <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 1rem" }}>
              {searchTerm || priorityFilter !== "all" || activeTab !== "all"
                ? "Try adjusting your filters or search term."
                : "No matters have been escalated or resources requested yet."}
            </p>
            {!isSuperAdmin && (
              <Link to="/escalations/new" className="btn btn-primary btn-sm">
                <FiPlusCircle size={13} /> Submit New Request
              </Link>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc" }}>
                  <th style={{ width: 120 }}>Ticket ID</th>
                  {isSuperAdmin && <th>College</th>}
                  <th>Type & Category</th>
                  <th>Subject & Details</th>
                  <th style={{ width: 90 }}>Priority</th>
                  <th style={{ width: 130 }}>Status</th>
                  <th>Super Admin Action</th>
                  <th style={{ width: 100, textAlign: "right" }}>Submitted</th>
                  <th style={{ width: 80, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const isSubmitted = ["submitted", "under_review"].includes(r.status);
                  return (
                    <tr
                      key={r._id}
                      onClick={() => navigate(`/escalations/${r._id}`)}
                      style={{ cursor: "pointer", transition: "background 0.1s" }}
                      className="hover-row"
                    >
                      {/* Ticket Number */}
                      <td>
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            fontSize: "12px",
                            color: "#334155",
                          }}
                        >
                          {r.ticketNumber || "N/A"}
                        </span>
                      </td>

                      {/* College (Super Admin only) */}
                      {isSuperAdmin && (
                        <td>
                          <span style={{ fontWeight: 600, fontSize: "12.5px" }}>
                            {r.college?.code || r.college?.name || "College"}
                          </span>
                        </td>
                      )}

                      {/* Type & Category */}
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "10.5px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              backgroundColor: r.type === "escalation" ? "#fef3c7" : "#e0e7ff",
                              color: r.type === "escalation" ? "#92400e" : "#3730a3",
                            }}
                          >
                            {r.type === "escalation" ? "Escalation" : "Resource"}
                          </span>
                        </div>
                        <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                          {r.category}
                        </div>
                      </td>

                      {/* Subject & Summary */}
                      <td>
                        <div style={{ fontWeight: 600, fontSize: "13px", color: "#0f172a" }}>
                          {r.title}
                        </div>
                        <div
                          style={{
                            fontSize: "11.5px",
                            color: "#64748b",
                            marginTop: "2px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 320,
                          }}
                        >
                          {r.type === "resource_request" && r.itemsRequested?.length > 0
                            ? `Items: ${r.itemsRequested.map((i) => `${i.itemName} (x${i.quantity})`).join(", ")}`
                            : r.description}
                        </div>
                      </td>

                      {/* Priority */}
                      <td>{getPriorityBadge(r.priority)}</td>

                      {/* Status */}
                      <td>{getStatusBadge(r.status)}</td>

                      {/* Super Admin Action */}
                      <td>
                        {r.superAdminAction?.actionTaken ? (
                          <div style={{ fontSize: "12px", color: "#4f46e5" }}>
                            <span style={{ fontWeight: 600 }}>Action: </span>
                            <span style={{ color: "#334155" }}>{r.superAdminAction.actionTaken}</span>
                          </div>
                        ) : (
                          <span style={{ fontSize: "12px", color: "#94a3b8", fontStyle: "italic" }}>
                            Pending response
                          </span>
                        )}
                        {r.superAdminAction?.allocatedResources && (
                          <div style={{ fontSize: "11px", color: "#059669", marginTop: "2px" }}>
                            <strong>Allocated:</strong> {r.superAdminAction.allocatedResources}
                          </div>
                        )}
                      </td>

                      {/* Submitted Date */}
                      <td style={{ textAlign: "right", fontSize: "12px", color: "#64748b" }}>
                        {new Date(r.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link
                            to={`/escalations/${r._id}`}
                            className="btn-icon"
                            title="View Details"
                            style={{ color: "#4f46e5" }}
                          >
                            <FiEye size={15} />
                          </Link>
                          {(!isSuperAdmin && isSubmitted) && (
                            <button
                              type="button"
                              onClick={(e) => handleDelete(r._id, e)}
                              className="btn-icon"
                              title="Delete request"
                              style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer" }}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.85rem 1.25rem",
              borderTop: "1px solid #f1f5f9",
              fontSize: "12.5px",
              color: "#64748b",
            }}
          >
            <span>
              Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                className="btn btn-outline"
                style={{ padding: "3px 10px", fontSize: "12px" }}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                className="btn btn-outline"
                style={{ padding: "3px 10px", fontSize: "12px" }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
